import React from 'react';
import { AppendixValues } from './types';

export interface Content13WAppendixProps {
  mainContractNumber: string;
  mainContractDate: string;
  values: AppendixValues;
}

const Content13WAppendix: React.FC<Content13WAppendixProps> = ({
  mainContractNumber,
  mainContractDate,
  values,
}) => {
  // Helper to render placeholder if value is empty
  const renderValue = (value: string, width: string = 'w-32') => {
    if (value) return value;
    return <span className={`inline-block border-b border-gray-400 ${width} h-px ml-1 translate-y-[-2px]`}></span>;
  };

  return (
    <div className="!space-y-5 !text-sm">
      {/* ĐIỀU 1 */}
      <div className="pdf-block">
        <h4 className="!font-bold !text-gray-900 dark:!text-white !mb-2">ĐIỀU 1. MỤC ĐÍCH PHỤ LỤC</h4>
        <div className="!text-gray-700 dark:!text-gray-300 !space-y-2 !pl-4">
          <p>
            Phụ lục này được lập nhằm bổ sung điều khoản liên quan đến giá trị tham gia ghi trong Hợp đồng số {renderValue(mainContractNumber)} ký ngày {renderValue(mainContractDate, 'w-24')}.
          </p>
        </div>
      </div>

      {/* ĐIỀU 2 */}
      <div className="pdf-block">
        <h4 className="!font-bold !text-gray-900 dark:!text-white !mb-2">ĐIỀU 2. NỘI DUNG ĐIỀU CHỈNH, BỔ SUNG</h4>
        <div className="!text-gray-700 dark:!text-gray-300 !space-y-2 !pl-4">
          <p>
            Hai bên thống nhất điều chỉnh, bổ sung một số điều khoản của Hợp đồng số {renderValue(mainContractNumber)} ký ngày {renderValue(mainContractDate, 'w-24')} như sau:
          </p>
        </div>
      </div>

      {/* A. NỘI DUNG ĐIỀU CHỈNH */}
      <div className="pdf-block">
        <h5 className="!font-bold !text-gray-900 dark:!text-white !mb-2">A. NỘI DUNG ĐIỀU CHỈNH:</h5>
        <div className="!text-gray-700 dark:!text-gray-300 !space-y-2 !pl-4">
          <p>
            <strong>1.</strong> Điều chỉnh Khoản 4 Điều I: Doanh thu ròng là doanh thu gộp phát sinh từ các hoạt động đào tạo, cấp quyền sử dụng phần mềm huấn luyện giao dịch tự động và các dịch vụ liên quan trực tiếp đến chương trình 13 tuần thử thách (13W challenge) nằm trong dự án LUMIR sau khi đã trừ đi các khoản chi phí liên quan và các khoản giảm trừ như: chiết khấu hàng bán, giảm giá hàng bán, thuế và các khoản khấu trừ khác.
          </p>
          <p><strong>2.</strong> Điều chỉnh quy mô dự án: Tối đa 68 đối tác.</p>
          <p>
            <strong>3.</strong> Điều chỉnh quỹ chia sẻ: 5% trên tổng doanh thu ròng trong kỳ để phân bổ cho các đối tác vượt qua chương trình 13 tuần thử thách (13W challenge).
          </p>
          <p>
            <strong>4.</strong> Điều chỉnh Khoản 3 Điều III: Nguồn quỹ dùng để chi trả cho các đối tác đang ở trạng thái hiệu lực theo quy định tại Khoản 4 Điều III của hợp đồng.
          </p>
          <p><strong>5.</strong> Điều chỉnh Điểm b, Điểm c Khoản 4 Điều 3:</p>
          <div className="!pl-4 !space-y-1">
            <p>- Khi đủ 68 đối tác, mỗi đối tác nhận 5%/68 ≈ 0,0735294117%/kỳ.</p>
            <p>- Nếu số đối tác hiệu lực thực tế là N &lt; 68, thì tỉ lệ phần trăm mỗi đối tác nhận được là 5%/N.</p>
          </div>
          <p>
            <strong>6.</strong> Điều chỉnh Khoản 5 Điều III: Khoản 20% Bên B chia lại từ thu nhập cá nhân cho Bên A và nguồn quỹ trích từ 5% trên tổng doanh thu ròng là hai dòng tiền riêng, không bù trừ (trừ khi có quy định khác tại Khoản 4 Điều IV của hợp đồng).
          </p>
        </div>
      </div>

      {/* B. NỘI DUNG BỔ SUNG */}
      <div className="pdf-block">
        <h5 className="!font-bold !text-gray-900 dark:!text-white !mb-2">B. NỘI DUNG BỔ SUNG:</h5>
        <div className="!text-gray-700 dark:!text-gray-300 !space-y-2 !pl-4">
          <p>
            <strong>1.</strong> Bổ sung Khoản 3 Điều I: Giá trị tham gia đã bao gồm thuế giá trị gia tăng (VAT) với mức thuế suất 10%. Bên A có trách nhiệm phát hành hóa đơn giá trị gia tăng (GTGT) hợp pháp tương ứng với giá trị Bên B thanh toán. Cụ thể như sau:
          </p>
          <div className="!pl-4 !space-y-1">
            <p>
              <strong>a)</strong> Giá trị tham gia trước thuế: {values.preTaxValue} VND (Bằng chữ: Mười một triệu, tám trăm mười tám nghìn, một trăm tám mươi hai đồng)
            </p>
            <p>
              <strong>b)</strong> Tiền thuế VAT 10%: {values.vatAmount} VND (Bằng chữ: Một triệu, một trăm tám mươi mốt nghìn, tám trăm mười tám đồng)
            </p>
            <p>
              <strong>c)</strong> Giá trị tham gia bao gồm 10% VAT: {values.totalValue} VND (Bằng chữ: Mười ba triệu đồng chẵn)
            </p>
          </div>
          <p><strong>2.</strong> Các nội dung khác ghi trong Hợp đồng không thay đổi.</p>
        </div>
      </div>

      {/* ĐIỀU 3 */}
      <div className="pdf-block">
        <h4 className="!font-bold !text-gray-900 dark:!text-white !mb-2">ĐIỀU 3. HIỆU LỰC THI HÀNH</h4>
        <div className="!text-gray-700 dark:!text-gray-300 !space-y-2 !pl-4">
          <p>
            <strong>1.</strong> Phụ lục này là một phần không tách rời của Hợp đồng số {renderValue(mainContractNumber)} ký ngày {renderValue(mainContractDate, 'w-24')}.
          </p>
          <p>
            <strong>2.</strong> Phụ lục này có hiệu lực kể từ ngày ký và được lập thành hai (02) bản, mỗi bên giữ một (01) bản có giá trị pháp lý như nhau.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Content13WAppendix;
